// 열 매핑을 한 줄에 적용하는 규칙(금액 ÷ 수량 · 경비 더하기) — 엑셀 가져오기와 붙여넣기가 이 한 벌을 쓴다.

import { parseMoney } from "../editor/cell-number";
import type { RowField } from "../editor/paste";
import { FilledItemInput } from "../editor/types";
import { MONEY_MAX } from "../schema";
import { ColumnMap, ImportField } from "./types";

const ROW_FIELDS = new Set<ImportField>([
  "space_code",
  "scope",
  "item",
  "unit",
  "qty",
  "material_unit_price",
  "labor_unit_price",
  "cost_unit_price",
  "note",
]);

/** 열 매핑 중 칸 그대로 담는 열만 남긴다(금액·경비·공종 열은 null). */
export const rowFieldsOf = (columns: ColumnMap): (RowField | null)[] =>
  columns.map((f) => (f !== null && ROW_FIELDS.has(f) ? (f as RowField) : null));

// 금액 열만 있는 양식 — 단가 = 금액 ÷ 수량(수량이 없으면 1). 못 읽으면 null.
const priceFromAmount = (
  row: FilledItemInput,
  raw: string,
  price: "material_unit_price" | "labor_unit_price"
): FilledItemInput | null => {
  const amount = parseMoney(raw);
  if (amount === null || amount > MONEY_MAX) return null;
  if (amount === 0) return row;
  const qty = row.qty > 0 ? row.qty : 1;
  return { ...row, qty, [price]: Math.round(amount / qty) };
};

export interface AmountApplied {
  row: FilledItemInput;
  /** 못 읽은 금액·경비 칸 수. */
  unclear: number;
  /** 금액 열로 단가를 구했나. */
  priced: boolean;
  /** 경비를 노무 단가에 더했나. */
  expenseMerged: boolean;
}

/** 단가 열이 없는 자재·노무 금액 열은 수량으로 나눠 단가로, 경비 단가는 노무 단가에 더한다. */
export const applyAmountColumns = (
  start: FilledItemInput,
  cells: string[],
  columns: ColumnMap
): AmountApplied => {
  let row = start;
  let unclear = 0;
  let priced = false;
  let expenseMerged = false;
  const amountCols = (
    [
      ["material_amount", "material_unit_price"],
      ["labor_amount", "labor_unit_price"],
    ] as const
  )
    .map(([amount, price]) => ({ col: columns.indexOf(amount), price }))
    .filter((a) => a.col >= 0 && !columns.includes(a.price));
  for (const a of amountCols) {
    const next = priceFromAmount(row, cells[a.col] ?? "", a.price);
    if (next === null) unclear += 1;
    else if (next !== row) {
      row = next;
      priced = true;
    }
  }
  const expenseCol = columns.indexOf("expense_unit_price");
  if (expenseCol >= 0) {
    const expense = parseMoney(cells[expenseCol] ?? "");
    if (expense === null || row.labor_unit_price + expense > MONEY_MAX) {
      unclear += 1;
    } else if (expense > 0) {
      row = { ...row, labor_unit_price: row.labor_unit_price + expense };
      expenseMerged = true;
    }
  }
  return { row, unclear, priced, expenseMerged };
};
